/**
 * 변경 이력 — 독립 메뉴 /audit-log (전 권한 조회, 금액은 viewer 마스킹, 되돌리기는 admin).
 *
 * audit_log 테이블 조회 — 트리거가 자동 적재한 운영 데이터 변경 내역.
 *
 * 필터:
 *   - 테이블 (드롭다운, distinctTables)
 *   - 사용자ID (텍스트)
 *   - 동작 (INSERT/UPDATE/DELETE)
 *   - 기간 (from / to, YYYY-MM-DD)
 *
 * 표:
 *   - 시각, 테이블, 행PK, 동작, 필드, 이전→이후, 사용자
 *
 * 페이지네이션 — 50건/페이지 기본, [이전] [다음] 버튼.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as adminApi from '../lib/admin-api';
import type { AuditLogParams, AuditLogRow } from '../lib/admin-api';
import { ApiCallError } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { DateInput } from './DateInput';
import { ResetButton } from './ResetButton';
import {
  getMenuLabel,
  getFieldLabel,
  getRowIdentifier,
  formatAuditValue,
  TABLE_META,
} from '../lib/audit-labels';
import { LoadingLines } from './ui/Skeleton';

const ACTION_BADGE: Record<string, string> = {
  INSERT: 'bg-emerald-100 text-emerald-700',
  UPDATE: 'bg-blue-100 text-blue-700',
  DELETE: 'bg-rose-100 text-rose-700',
};

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${yy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
}

export function AuditLogTab() {
  const qc = useQueryClient();
  const { user } = useAuth();
  // 되돌리기는 admin 만 — viewer/sales 는 조회만 (운영 정책)
  const isAdmin = user?.role === 'admin';
  const [filters, setFilters] = useState<AuditLogParams>({
    page: 1,
    pageSize: 50,
  });
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  function toggleExpand(logId: number) {
    const next = new Set(expanded);
    if (next.has(logId)) next.delete(logId);
    else next.add(logId);
    setExpanded(next);
  }

  const q = useQuery({
    queryKey: ['admin', 'audit-log', filters],
    queryFn: () => adminApi.getAuditLog(filters),
    staleTime: 10_000,
  });

  const revertMut = useMutation({
    mutationFn: (input: { logId: number; force: boolean }) =>
      adminApi.revertAuditLog(input.logId, input.force),
    onSuccess: () => {
      qc.invalidateQueries();
    },
  });

  async function handleRevert(row: AuditLogRow) {
    const label = describeForConfirm(row);
    // 「추가」를 되돌리면 그 줄이 지워진다 — 사용자 계정이면 계정이 통째로 없어진다 (v2.232.0 · 점검 ③ F-30)
    const insertWarn = row.action === 'INSERT'
      ? `\n\n⚠ 「추가」를 되돌리면 그 줄이 지워집니다${row.tableName === 'app_user' ? ' — 사용자 계정이면 그 계정이 없어져 로그인할 수 없습니다' : ''}.`
      : '';
    if (!window.confirm(`이 변경을 되돌릴까요?\n\n${label}\n\n취소 시 영향: 데이터가 그 직전 상태로 복원됩니다.${insertWarn}`)) {
      return;
    }
    try {
      const res = await revertMut.mutateAsync({ logId: row.logId, force: false });
      window.alert(`되돌리기 완료\n\n${res.action} · ${res.table} · ${res.rowPk}` +
        (res.field ? `\n필드: ${res.field} → ${res.oldValue ?? '∅'}` : '') +
        `\n영향 행수: ${res.rowsAffected}`);
    } catch (e) {
      const err = e as ApiCallError;
      // 더 최신 변경이 있을 때 — force 재시도 묻기
      if (err.message?.includes('더 최신 변경')) {
        if (window.confirm(`${err.message}\n\n그래도 진행할까요?`)) {
          try {
            const res = await revertMut.mutateAsync({ logId: row.logId, force: true });
            window.alert(`되돌리기 완료 (강제)\n\n${res.action} · ${res.table} · ${res.rowPk}`);
          } catch (e2) {
            window.alert(`되돌리기 실패: ${(e2 as Error).message}`);
          }
        }
        return;
      }
      window.alert(`되돌리기 실패\n\n${err.message}`);
    }
  }

  function patchFilters(p: Partial<AuditLogParams>) {
    setFilters((cur) => ({ ...cur, ...p, page: 1 }));
  }

  const total = q.data?.page.total ?? 0;
  const pageSize = filters.pageSize ?? 50;
  const page = filters.page ?? 1;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      {/* 필터 */}
      <div className="bg-white rounded shadow-sm border border-slate-200 p-4">
        <div className="grid grid-cols-5 gap-3 text-sm">
          <div>
            <label className="ui-label">메뉴</label>
            <select
              value={filters.tableName ?? ''}
              onChange={(e) =>
                patchFilters({ tableName: e.target.value || undefined })
              }
              className="ui-input w-full mt-1"
            >
              <option value="">— 전체 —</option>
              {(q.data?.distinctTables ?? []).map((t) => (
                <option key={t} value={t}>
                  {getMenuLabel(t)}
                  {TABLE_META[t] ? '' : ` (${t})`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="ui-label">사용자ID</label>
            <input
              value={filters.userId ?? ''}
              onChange={(e) =>
                patchFilters({ userId: e.target.value || undefined })
              }
              placeholder="예: sales / admin / js"
              className="ui-input w-full mt-1"
            />
          </div>
          <div>
            <label className="ui-label">동작</label>
            <select
              value={filters.action ?? ''}
              onChange={(e) =>
                patchFilters({
                  action: (e.target.value || undefined) as AuditLogParams['action'],
                })
              }
              className="ui-input w-full mt-1"
            >
              <option value="">— 전체 —</option>
              <option value="INSERT">INSERT</option>
              <option value="UPDATE">UPDATE</option>
              <option value="DELETE">DELETE</option>
            </select>
          </div>
          <div>
            <label className="ui-label">시작일</label>
            <DateInput
              value={filters.from ?? ''}
              onChange={(v) => patchFilters({ from: v || undefined })}
              inputClassName="w-full border border-slate-300 rounded px-2 py-1.5"
              className="mt-1"
            />
          </div>
          <div>
            <label className="ui-label">종료일</label>
            <DateInput
              value={filters.to ?? ''}
              onChange={(v) => patchFilters({ to: v || undefined })}
              inputClassName="w-full border border-slate-300 rounded px-2 py-1.5"
              className="mt-1"
            />
          </div>
        </div>
        <div className="flex items-center justify-between mt-3 text-xs">
          <ResetButton
            onClick={() =>
              setFilters({ page: 1, pageSize: filters.pageSize ?? 50 })
            }
            label="↺ 필터 초기화"
            title="테이블/사용자/기간 필터 모두 초기 상태로"
          />
          <div className="text-slate-500">
            총 {total.toLocaleString()} 건
            {q.isFetching && <span className="ml-2 text-slate-400"><LoadingLines /></span>}
          </div>
        </div>
      </div>

      {/* 표 — ⚠overflow-hidden 이면 좁은 화면(1366·1280)에서 맨 오른쪽 [↩ 되돌리기] 가 잘려 못 눌렀다 (v2.229.3 · 점검 H-5) */}
      <div className="bg-white rounded shadow-sm border border-slate-200 overflow-x-auto">
        {q.isLoading && (
          <div className="text-center text-slate-400 py-12"><LoadingLines /></div>
        )}
        {q.isError && (
          <div className="text-center text-red-600 py-12">
            오류: {(q.error as Error).message}
          </div>
        )}
        {!q.isLoading && !q.isError && (q.data?.data.length ?? 0) === 0 && (
          <div className="text-center text-slate-400 py-12">변경 이력이 없습니다.</div>
        )}
        {(q.data?.data.length ?? 0) > 0 && (
          <table className="data-table w-full text-[13px]">
            <thead>
              <tr>
                <th className="text-left whitespace-nowrap">시각</th>
                <th className="text-left">메뉴</th>
                <th className="text-left">항목 (식별자)</th>
                <th className="text-left">동작</th>
                <th className="text-left">변경 항목</th>
                <th className="text-left">이전 → 이후</th>
                <th className="text-left">사용자</th>
                <th className="text-left">관리</th>
              </tr>
            </thead>
            <tbody>
              {q.data!.data.map((r) => (
                <Row
                  key={r.logId}
                  row={r}
                  expanded={expanded.has(r.logId)}
                  onToggle={() => toggleExpand(r.logId)}
                  onRevert={() => handleRevert(r)}
                  reverting={revertMut.isPending}
                  isAdmin={isAdmin}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 페이지네이션 */}
      {total > pageSize && (
        <div className="flex items-center justify-center gap-2 text-sm">
          <button
            type="button"
            onClick={() => setFilters((c) => ({ ...c, page: Math.max(1, page - 1) }))}
            disabled={page <= 1}
            className="btn btn-secondary"
          >
            ← 이전
          </button>
          <span className="text-slate-600">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() =>
              setFilters((c) => ({ ...c, page: Math.min(totalPages, page + 1) }))
            }
            disabled={page >= totalPages}
            className="btn btn-secondary"
          >
            다음 →
          </button>
        </div>
      )}
    </div>
  );
}

function Row({
  row,
  expanded,
  onToggle,
  onRevert,
  reverting,
  isAdmin,
}: {
  row: AuditLogRow;
  expanded: boolean;
  onToggle: () => void;
  onRevert: () => void;
  reverting: boolean;
  isAdmin: boolean;
}) {
  const cls = ACTION_BADGE[row.action] ?? 'bg-slate-100 text-slate-600';
  const hasRowData = !!row.rowData;
  const isBackfilled = row.rowDataSource === 'current';
  // 되돌릴 수 있는 경우:
  //   UPDATE: 항상 가능 (old_value 로 복원)
  //   INSERT: 항상 가능 (행 삭제 — row_data 불필요)
  //   DELETE: row_data 가 'audit' 출처일 때만 (마이그 이전 행은 보강 안 됨)
  const canRevertByAction =
    row.action === 'UPDATE' ||
    row.action === 'INSERT' ||
    (row.action === 'DELETE' && row.rowDataSource === 'audit');
  // 기간 cap — 변경 후 3일 이내만 가능 (백엔드 REVERT_MAX_AGE_DAYS 와 동기화)
  const REVERT_MAX_AGE_DAYS = 3;
  const ageDays = (Date.now() - new Date(row.changedAt).getTime()) / (1000 * 60 * 60 * 24);
  const withinAge = ageDays <= REVERT_MAX_AGE_DAYS;
  // 되돌리기는 admin 만 — 일반 계정은 조회만
  // 되돌리기를 지원하지 않는 표(가격표·나라장터·수출입·개발 계획 — 보기만)는 단추를 달지 않는다 (v2.231.0 · F-19)
  const canRevert = isAdmin && canRevertByAction && withinAge && row.revertable !== false;
  // 행 식별자 (rowData 에서 사람 친화적 필드 추출 — 예: 영업진행이면 엔드유저명)
  const identifier = getRowIdentifier(row.tableName, row.rowData as Record<string, unknown> | null);
  const menuLabel = getMenuLabel(row.tableName);
  const isMappedTable = !!TABLE_META[row.tableName];

  // 동작 한글 + 색상 (배지 유지하되 한글 라벨 병기)
  const actionKr = row.action === 'INSERT' ? '입력' : row.action === 'UPDATE' ? '수정' : row.action === 'DELETE' ? '삭제' : row.action;

  return (
    <>
      <tr>
        <td className="whitespace-nowrap text-xs">
          {fmtDateTime(row.changedAt)}
        </td>
        <td className="whitespace-nowrap text-xs" title={isMappedTable ? row.tableName : '매핑 없는 테이블'}>
          {menuLabel}
          {!isMappedTable && <span className="ml-1 text-slate-400 font-mono">({row.tableName})</span>}
        </td>
        <td className="whitespace-nowrap text-xs">
          <span className="font-mono text-slate-600">{row.rowPk}</span>
          {identifier && (
            <span className="ml-1.5 text-slate-800 inline-block max-w-[14rem] truncate align-bottom" title={`행 식별자 (rowData 에서 추출) — ${identifier}`}>
              {identifier}
            </span>
          )}
        </td>
        <td>
          <span className={`text-xs px-2 py-0.5 rounded ${cls}`}>{actionKr}</span>
        </td>
        <td className="text-xs text-slate-700" title={row.fieldName ?? ''}>
          {row.fieldName ? getFieldLabel(row.tableName, row.fieldName) : '-'}
        </td>
        <td className="text-xs [overflow-wrap:anywhere]">
          {row.action === 'UPDATE' && row.fieldName ? (
            <span>
              <span className="text-rose-600 line-through">{formatAuditValue(row.oldValue)}</span>
              {' → '}
              <span className="text-emerald-700 font-medium">{formatAuditValue(row.newValue)}</span>
            </span>
          ) : (
            <span className="text-slate-400">-</span>
          )}
        </td>
        <td className="whitespace-nowrap">
          <div className="text-slate-700">{row.userName ?? row.userId ?? '-'}</div>
          {row.userName && row.userId && (
            <div className="text-[11px] text-slate-500 font-mono">{row.userId}</div>
          )}
        </td>
        <td className="whitespace-nowrap">
          <div className="flex items-center gap-2">
            {hasRowData && (
              <button
                type="button"
                onClick={onToggle}
                className="text-xs text-blue-600 hover:underline"
                title={isBackfilled
                  ? '이 행의 현재 상태를 보여줍니다 (이력 시점 X)'
                  : '이력 시점의 행 데이터를 보여줍니다'}
              >
                {expanded ? '▲ 접기' : '▼ 전체 보기'}
                {isBackfilled && <span className="ml-0.5 text-slate-400">(현재)</span>}
              </button>
            )}
            {canRevert && (
              <button
                type="button"
                onClick={onRevert}
                disabled={reverting}
                className="btn btn-warning btn-sm"
                title="이 변경 직전 상태로 되돌리기"
              >
                ↩ 되돌리기
              </button>
            )}
            {canRevertByAction && !withinAge && (
              <span
                className="text-xs text-slate-500 italic"
                title={`되돌리기는 변경 후 ${REVERT_MAX_AGE_DAYS}일 이내만 가능 (이 변경은 약 ${Math.floor(ageDays)}일 전)`}
              >
                🕒 기간 초과
              </span>
            )}
            {!canRevertByAction && !hasRowData && (
              <span
                className="text-xs text-slate-400"
                title={row.action === 'DELETE'
                  ? '마이그레이션 이전 삭제 — 행 데이터가 없어 복원 불가'
                  : '관리 화이트리스트 외 테이블'}
              >
                -
              </span>
            )}
          </div>
        </td>
      </tr>
      {expanded && hasRowData && (
        <tr>
          <td colSpan={8} className="bg-slate-50 border-l-4 border-blue-300 px-4 py-3">
            <RowDataDetail
              tableName={row.tableName}
              data={row.rowData!}
              source={row.rowDataSource}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function describeForConfirm(row: AuditLogRow): string {
  const head = `${row.tableName} #${row.rowPk}`;
  if (row.action === 'UPDATE' && row.fieldName) {
    return `${head}\n${row.fieldName}: ${row.oldValue ?? '∅'} ← ${row.newValue ?? '∅'} 로 되돌립니다.`;
  }
  if (row.action === 'INSERT') {
    return `${head}\n이 INSERT 를 취소 → 행이 삭제됩니다.`;
  }
  if (row.action === 'DELETE') {
    return `${head}\n삭제된 행을 복원합니다.`;
  }
  return head;
}

function RowDataDetail({
  tableName,
  data,
  source,
}: {
  tableName: string;
  data: Record<string, unknown>;
  source: 'audit' | 'current' | null;
}) {
  const entries = Object.entries(data).filter(([, v]) => v !== null && v !== undefined);
  const isCurrent = source === 'current';
  return (
    <div>
      <div className={`text-xs mb-2 ${isCurrent ? 'text-amber-700' : 'text-slate-500'}`}>
        {isCurrent ? (
          <>
            ⚠ <strong>현재 행 상태</strong> ({entries.length}개 항목) — 마이그레이션 이전 변경이라
            이력 시점의 데이터는 보존돼 있지 않습니다. 이후 변경들이 모두 반영된 현재 모습입니다.
          </>
        ) : (
          <>이력 시점의 행 데이터 ({entries.length}개 항목) — 되돌리기 시 이 값으로 복원됨</>
        )}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1 text-xs">
        {entries.map(([k, v]) => {
          const label = getFieldLabel(tableName, k);
          return (
            <div key={k} className="flex gap-1.5 border-b border-slate-200 py-0.5">
              <span className="text-slate-500 min-w-[110px] shrink-0" title={k}>
                {label}
              </span>
              <span className="text-slate-800 break-all">{formatAuditValue(v)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
